/**
 * Symptom tracking utilities for Correly.
 *
 * Each symptom is rated on a 0–10 scale.
 * Practitioners can toggle which symptoms are tracked per client.
 * These helpers define the symptom catalog, compute scores, and format labels.
 */

/**
 * Master list of all trackable symptoms.
 * Each symptom has a key (used in the database), label, description,
 * and scale anchors for the 0 and 10 ends.
 */
export const SYMPTOM_CATALOG = [
  {
    key: 'sleep_quality',
    label: 'Sleep Quality',
    subtitle: 'Last night',
    low: 'Terrible',
    high: 'Excellent',
    icon: '🌙',
    color: '#5C4A9E',
  },
  {
    key: 'energy',
    label: 'Energy',
    subtitle: '',
    low: 'None',
    high: 'Vibrant',
    icon: '⚡',
    color: '#C2662D',
  },
  {
    key: 'fatigue',
    label: 'Fatigue',
    subtitle: '',
    low: 'None',
    high: 'Severe',
    icon: '😴',
    color: '#8B7355',
    inverted: true, // Higher = worse for this symptom
  },
  {
    key: 'bloating',
    label: 'Bloating',
    subtitle: '',
    low: 'None',
    high: 'Severe',
    icon: '🫧',
    color: '#B07D4B',
    inverted: true,
  },
  {
    key: 'digestive_comfort',
    label: 'Digestive Comfort',
    subtitle: '',
    low: 'Very poor',
    high: 'Great',
    icon: '🍃',
    color: '#3D7A5E',
  },
  {
    key: 'hunger_cravings',
    label: 'Hunger / Cravings',
    subtitle: '',
    low: 'None',
    high: 'Intense',
    icon: '🍽',
    color: '#D4853A',
    inverted: true,
  },
  {
    key: 'mental_clarity',
    label: 'Mental Clarity',
    subtitle: '',
    low: 'Foggy',
    high: 'Crystal clear',
    icon: '🧠',
    color: '#4A8FB5',
  },
  {
    key: 'mood',
    label: 'Mood',
    subtitle: 'Irritability, anxiety',
    low: 'Very low',
    high: 'Great',
    icon: '😊',
    color: '#7A6651',
  },
  {
    key: 'skin',
    label: 'Skin',
    subtitle: 'Acne, rashes',
    low: 'Severe issues',
    high: 'Clear',
    icon: '✨',
    color: '#C77986',
  },
];

/**
 * Default enabled symptoms (all enabled by default).
 * Practitioners can toggle these per client.
 */
export const DEFAULT_ENABLED_SYMPTOMS = SYMPTOM_CATALOG.map((s) => s.key);

/**
 * Get symptom definition by key.
 */
export function getSymptom(key) {
  return SYMPTOM_CATALOG.find((s) => s.key === key);
}

/**
 * Get a descriptive label for a 0-10 value.
 */
export function getScoreLabel(value) {
  if (value === 0) return 'None';
  if (value <= 2) return 'Mild';
  if (value <= 4) return 'Low';
  if (value <= 6) return 'Moderate';
  if (value <= 8) return 'High';
  return 'Severe';
}

/**
 * Calculate average symptom score from a daily log entry.
 * Only averages non-inverted symptoms (where higher = better).
 * Inverted symptoms (fatigue, bloating, cravings) are flipped (10 - value) before averaging.
 * Returns a value between 0 and 10, or null if no data.
 *
 * @param {object} log - daily log object with symptom keys
 * @param {string[]} enabledSymptoms - which symptoms to include
 */
export function calculateWellnessScore(log, enabledSymptoms = null) {
  const symptoms = enabledSymptoms || DEFAULT_ENABLED_SYMPTOMS;
  const values = [];

  for (const key of symptoms) {
    const val = log[key];
    if (val == null) continue;
    const def = getSymptom(key);
    // Normalize so higher always = better
    values.push(def?.inverted ? 10 - val : val);
  }

  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Get status color class based on a 0-10 value.
 */
export function getStatusLevel(value) {
  if (value >= 7) return 'good';
  if (value >= 4) return 'moderate';
  return 'poor';
}

/**
 * Calculate compliance percentage.
 * @param {number} completed - number of completed tasks
 * @param {number} total - total expected tasks
 */
export function calculateCompliance(completed, total) {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}
